/* =============================================================================
 * Collection grid mapped pre-order quick-add
 * -----------------------------------------------------------------------------
 * Delegated click handling for featured-collection-product-callout sections.
 * Requires mapped-preorder-core.js and section JSON / card payloads from Liquid.
 * ========================================================================== */

(function () {
  'use strict';

  if (window.__sscMappedPreorderCollectionLoaded) return;
  window.__sscMappedPreorderCollectionLoaded = true;

  var Core = window.ssc && window.ssc.MappedPreorderCore;
  if (!Core) {
    console.error(
      '[ssc-mapped-preorder-collection]',
      'MappedPreorderCore missing — load mapped-preorder-core.js first',
    );
    return;
  }

  var LOG_PREFIX = '[ssc-mapped-preorder-collection]';

  /** @type {WeakMap<Element, object>} */
  var cardCache = new WeakMap();

  /** @type {Record<string, object>} */
  var pendingChoiceBySection = Object.create(null);

  var LOOX_RATING_SELECTOR = '.loox-rating';

  /** @type {WeakMap<Element, { ariaHidden: string|null, visibility: string, pointerEvents: string }>} */
  var looxRatingState = new WeakMap();

  function hideSectionLooxRatings(sectionRoot) {
    if (!sectionRoot) return;

    var ratings = sectionRoot.querySelectorAll(LOOX_RATING_SELECTOR);
    for (var i = 0; i < ratings.length; i++) {
      var el = ratings[i];
      if (looxRatingState.has(el)) continue;

      looxRatingState.set(el, {
        ariaHidden: el.getAttribute('aria-hidden'),
        visibility: el.style.visibility,
        pointerEvents: el.style.pointerEvents,
      });
      el.setAttribute('aria-hidden', 'true');
      el.style.visibility = 'hidden';
      el.style.pointerEvents = 'none';
    }
  }

  function showSectionLooxRatings(sectionRoot) {
    if (!sectionRoot) return;

    var ratings = sectionRoot.querySelectorAll(LOOX_RATING_SELECTOR);
    for (var i = 0; i < ratings.length; i++) {
      var el = ratings[i];
      var prev = looxRatingState.get(el);
      if (!prev) continue;

      if (prev.ariaHidden === null) {
        el.removeAttribute('aria-hidden');
      } else {
        el.setAttribute('aria-hidden', prev.ariaHidden);
      }
      el.style.visibility = prev.visibility;
      el.style.pointerEvents = prev.pointerEvents;
      looxRatingState.delete(el);
    }
  }

  function parseJsonScript(root, selector) {
    return Core.parseJsonScript(root, selector);
  }

  var SECTION_CONFIG_SELECTOR =
    'script[data-ssc-collection-preorder-section-config]';

  function getSectionConfig(sectionRoot) {
    if (!sectionRoot) return null;

    var config = parseJsonScript(sectionRoot, SECTION_CONFIG_SELECTOR);
    if (config) return config;

    var sectionId =
      sectionRoot.dataset && sectionRoot.dataset.sectionId;
    if (!sectionId) return null;

    var shopifySection = document.getElementById(
      'shopify-section-' + sectionId,
    );
    if (!shopifySection || shopifySection === sectionRoot) return null;

    return parseJsonScript(shopifySection, SECTION_CONFIG_SELECTOR);
  }

  function getModal(sectionId) {
    var modals = window.ssc && window.ssc.collectionPreorderModals;
    return modals ? modals[String(sectionId)] : null;
  }

  function getModalEl(sectionRoot, config) {
    if (!sectionRoot || !config) return null;
    var modalId = config.modalId;
    if (!modalId) return null;
    return sectionRoot.querySelector('#' + modalId) || document.getElementById(modalId);
  }

  function lipKeysFromConfig(config) {
    return {
      purchaseType: config.lipPurchaseTypeKey || 'Purchase Type',
      shipDate: config.lipShipDateKey || 'Estimated Ship Date',
      parentHandle: config.lipParentHandleKey || '_Parent Product Handle',
      parentVariantId:
        config.lipParentVariantIdKey || '_Parent Variant ID',
      color: config.lipColorKey || 'Color',
      size: config.lipSizeKey || 'Size',
    };
  }

  function getCardData(card) {
    if (!card) return null;
    if (cardCache.has(card)) return cardCache.get(card);

    var mainVariants = parseJsonScript(card, '[data-ssc-collection-main-variants]');
    var mainOptions = parseJsonScript(card, '[data-ssc-collection-main-options]');
    var mainDateMap = Core.parseDateMap(
      card,
      '[data-ssc-collection-main-preorder-dates]',
    );

    var mappedVariants = parseJsonScript(
      card,
      '[data-ssc-collection-mapped-variants]',
    );
    var mappedOptions = parseJsonScript(
      card,
      '[data-ssc-collection-mapped-options]',
    );
    var mappedDateMap = mappedVariants
      ? Core.parseDateMap(card, '[data-ssc-collection-mapped-preorder-dates]')
      : null;

    var data = {
      mainVariants: Array.isArray(mainVariants) ? mainVariants : [],
      mainOptions: Array.isArray(mainOptions) ? mainOptions : [],
      mainDateMap: mainDateMap,
      mappedVariants: Array.isArray(mappedVariants) ? mappedVariants : null,
      mappedOptions: Array.isArray(mappedOptions) ? mappedOptions : null,
      mappedDateMap: mappedDateMap,
      parentHandle: card.getAttribute('data-main-product-handle') || '',
      mainColor: card.getAttribute('data-main-color') || '',
      hasMapped: !!card.getAttribute('data-mapped-product-handle'),
    };

    cardCache.set(card, data);
    return data;
  }

  function getProductTitle(card) {
    var titleEl = card.querySelector('.grid-product__title');
    if (titleEl) {
      var t = (titleEl.textContent || '').trim();
      if (t) return t;
    }
    return card.getAttribute('data-main-product-handle') || 'Item';
  }

  function formatSizeLabel(btn) {
    var raw = btn.getAttribute('data-value') || '';
    if (!raw) {
      var label = btn.querySelector('label');
      return label ? (label.textContent || '').trim() : '';
    }
    return raw;
  }

  function resolvePreorderVariant(btn, cardData, config) {
    var mainId = btn.getAttribute('data-main-variant-id');
    var mainVariant = Core.findVariantById(cardData.mainVariants, mainId);
    if (!mainVariant) return { variant: null, lookup: null, dateMap: null };

    var mappedId = btn.getAttribute('data-mapped-variant-id');
    if (mappedId && cardData.mappedVariants) {
      var mappedVariant = Core.findVariantById(
        cardData.mappedVariants,
        mappedId,
      );
      if (mappedVariant && mappedVariant.available === true) {
        var mappedLookup = Core.buildLookup(
          cardData.mappedVariants,
          cardData.mappedOptions,
          config.mappedColorOptionName,
          config.mappedSizeOptionName,
        );
        return {
          variant: mappedVariant,
          lookup: mappedLookup,
          dateMap: cardData.mappedDateMap || Core.normalizeDateMap(null),
          mainVariant: mainVariant,
        };
      }
    }

    if (cardData.mappedVariants && cardData.mappedOptions) {
      var lookup = Core.buildLookup(
        cardData.mappedVariants,
        cardData.mappedOptions,
        config.mappedColorOptionName,
        config.mappedSizeOptionName,
      );
      var resolved = Core.resolveMapped(
        mainVariant,
        cardData.mainOptions,
        config.mainColorOptionName,
        config.mainSizeOptionName,
        lookup,
      );
      if (resolved && resolved.available === true) {
        return {
          variant: resolved,
          lookup: lookup,
          dateMap: cardData.mappedDateMap || Core.normalizeDateMap(null),
          mainVariant: mainVariant,
        };
      }
    }

    var mainLookup = Core.buildLookup(
      cardData.mainVariants,
      cardData.mainOptions,
      config.mainColorOptionName,
      config.mainSizeOptionName,
    );
    return {
      variant: mainVariant,
      lookup: mainLookup,
      dateMap: cardData.mainDateMap,
      mainVariant: mainVariant,
    };
  }

  function resolveShipTextForButton(btn, preorderCtx, config) {
    var fromBtn = btn.getAttribute('data-preorder-date');
    if (fromBtn) {
      return Core.interpolateShipText(
        config.estimatedShipDateText,
        fromBtn,
      );
    }
    if (!preorderCtx || !preorderCtx.variant) return '';
    var dateStr = Core.resolveShipDate(
      preorderCtx.dateMap,
      preorderCtx.variant,
    );
    return Core.interpolateShipText(config.estimatedShipDateText, dateStr);
  }

  function addStock(btn, config) {
    var mainId = btn.getAttribute('data-main-variant-id');
    if (!mainId) return Promise.resolve();

    return Core.cartAdd(
      { id: mainId, quantity: 1 },
      { addToCartBtn: btn },
    );
  }

  function addPreorder(btn, cardData, config) {
    var preorderCtx = resolvePreorderVariant(btn, cardData, config);
    var variant = preorderCtx.variant;
    if (!variant || !variant.id) {
      console.warn(LOG_PREFIX, 'pre-order add aborted: no variant resolved');
      return Promise.resolve();
    }

    if (variant.available !== true) {
      console.warn(LOG_PREFIX, 'pre-order variant unavailable', variant.id);
      return Promise.resolve();
    }

    var shipText = resolveShipTextForButton(btn, preorderCtx, config);
    var parentHandle = cardData.hasMapped ? cardData.parentHandle : '';

    var properties = Core.buildProperties({
      enableProperties: config.enableLineItemProps !== false,
      purchaseTypeLabel: config.purchaseTypeLabel,
      shipDateTemplate: config.estimatedShipDateText,
      shipText: shipText,
      dateMap: preorderCtx.dateMap,
      matchedVariant: variant,
      lookup: preorderCtx.lookup,
      mainVariant: preorderCtx.mainVariant,
      parentHandle: parentHandle,
      lipKeys: lipKeysFromConfig(config),
    });

    return Core.cartAdd(
      {
        id: variant.id,
        quantity: 1,
        properties: properties,
      },
      { addToCartBtn: btn },
    );
  }

  function openChoiceModal(sectionRoot, config, ctx) {
    var sectionId = String(config.sectionId);
    pendingChoiceBySection[sectionId] = ctx;

    var modalEl = getModalEl(sectionRoot, config);
    if (!modalEl) return;

    var headingEl = modalEl.querySelector(
      '[data-ssc-collection-preorder-modal-heading]',
    );
    var selectionEl = modalEl.querySelector(
      '[data-ssc-collection-preorder-modal-selection]',
    );
    var shipEl = modalEl.querySelector(
      '[data-ssc-collection-preorder-modal-ship]',
    );

    if (selectionEl) {
      var parts = [ctx.productTitle];
      if (ctx.color) parts.push(ctx.color);
      if (ctx.size) parts.push(ctx.size);
      selectionEl.textContent = parts.join(' · ');
    }

    if (shipEl) {
      if (ctx.shipText) {
        shipEl.textContent = ctx.shipText;
        shipEl.removeAttribute('hidden');
      } else {
        shipEl.textContent = '';
        shipEl.setAttribute('hidden', 'hidden');
      }
    }

    if (headingEl && !headingEl.textContent.trim()) {
      headingEl.textContent = 'Choose how to add';
    }

    hideSectionLooxRatings(sectionRoot);

    var modal = getModal(sectionId);
    if (modal && typeof modal.open === 'function') {
      modal.open();
    } else {
      modalEl.classList.add('modal--is-active');
      modalEl.setAttribute('aria-hidden', 'false');
    }
  }

  function closeChoiceModal(sectionId, config, sectionRoot) {
    showSectionLooxRatings(sectionRoot);

    var modal = getModal(sectionId);
    if (modal && typeof modal.close === 'function') {
      modal.close();
    } else {
      var modalEl = getModalEl(sectionRoot, config);
      if (modalEl) {
        modalEl.classList.remove('modal--is-active');
        modalEl.setAttribute('aria-hidden', 'true');
      }
    }
    delete pendingChoiceBySection[sectionId];
  }

  function executeChoice(sectionId, choice, sectionRoot, config) {
    var ctx = pendingChoiceBySection[sectionId];
    if (!ctx) return;

    var btn = ctx.button;
    var cardData = ctx.cardData;

    closeChoiceModal(sectionId, config, sectionRoot);

    if (choice === 'stock') {
      addStock(btn, config);
    } else if (choice === 'preorder') {
      addPreorder(btn, cardData, config);
    }
  }

  function sectionShouldInit(config) {
    if (!config) return false;
    return !!(
      config.enablePreorderLabel || config.enablePreorderCollectionAtc
    );
  }

  function getLabelSublineTemplate(card, config) {
    var fromCard = card.getAttribute('data-preorder-label-subline-template');
    if (fromCard) return fromCard;
    return config.labelDateSublineText || 'Ships by {date}';
  }

  function updateLabelSublineForButton(btn, config) {
    var card = btn.closest('[data-ssc-collection-card]');
    if (!card || !card.hasAttribute('data-ssc-collection-preorder-label-subline-enabled')) {
      return;
    }

    var sublineEl = card.querySelector(
      '[data-ssc-collection-preorder-label-subline]',
    );
    if (!sublineEl) return;

    var dateFromBtn = btn.getAttribute('data-preorder-date') || '';
    var text = Core.interpolateShipText(
      getLabelSublineTemplate(card, config),
      dateFromBtn,
    );

    if (text) {
      sublineEl.textContent = text;
      sublineEl.removeAttribute('hidden');
    } else {
      sublineEl.textContent = '';
      sublineEl.setAttribute('hidden', 'hidden');
    }
  }

  function onSizeSublineInteraction(evt, sectionRoot, config) {
    var btn =
      evt.target && evt.target.closest
        ? evt.target.closest('.quickAddToCart')
        : null;
    if (!btn || !sectionRoot.contains(btn)) return;

    if (evt.type === 'mouseover') {
      var related = evt.relatedTarget;
      if (related && btn.contains(related)) return;
    }

    updateLabelSublineForButton(btn, config);
  }

  function bindLabelSublineHandlers(sectionRoot, config) {
    if (
      !config.enablePreorderLabel ||
      !config.labelShowDateSubline ||
      sectionRoot.dataset.sscCollectionPreorderSublineBound === '1'
    ) {
      return;
    }
    sectionRoot.dataset.sscCollectionPreorderSublineBound = '1';

    sectionRoot.addEventListener('mouseover', function (evt) {
      onSizeSublineInteraction(evt, sectionRoot, config);
    });
    sectionRoot.addEventListener('focusin', function (evt) {
      onSizeSublineInteraction(evt, sectionRoot, config);
    });
  }

  function onQuickAddClick(evt, sectionRoot, config) {
    var btn =
      evt.target && evt.target.closest
        ? evt.target.closest('.quickAddToCart')
        : null;
    if (!btn || !sectionRoot.contains(btn)) return;

    if (!btn.hasAttribute('data-main-variant-id')) return;

    evt.preventDefault();
    evt.stopPropagation();
    evt.stopImmediatePropagation();

    var mainAvailable = btn.getAttribute('data-main-available') === 'true';
    var mainInStock = btn.hasAttribute('data-main-in-stock')
      ? btn.getAttribute('data-main-in-stock') === 'true'
      : mainAvailable;
    var preorderAvailable =
      btn.getAttribute('data-preorder-available') === 'true';
    var mappedPreorderAvailable =
      btn.getAttribute('data-mapped-preorder-available') === 'true';

    if (!mainAvailable && !preorderAvailable) return;

    var card = btn.closest('[data-ssc-collection-card]');
    if (!card) return;

    var cardData = getCardData(card);
    if (!cardData) return;

    if (mainInStock && mappedPreorderAvailable) {
      var preorderCtx = resolvePreorderVariant(btn, cardData, config);
      openChoiceModal(sectionRoot, config, {
        button: btn,
        cardData: cardData,
        productTitle: getProductTitle(card),
        color: cardData.mainColor,
        size: formatSizeLabel(btn),
        shipText: resolveShipTextForButton(btn, preorderCtx, config),
      });
      return;
    }

    if (mainInStock) {
      addStock(btn, config);
      return;
    }

    if (preorderAvailable) {
      addPreorder(btn, cardData, config);
    }
  }

  function bindModalCloseRestore(sectionRoot, config) {
    if (!config || !config.modalId) return;
    if (sectionRoot.dataset.sscCollectionPreorderModalCloseBound === '1') {
      return;
    }
    sectionRoot.dataset.sscCollectionPreorderModalCloseBound = '1';

    document.addEventListener('modalClose.' + config.modalId, function () {
      showSectionLooxRatings(sectionRoot);
    });
  }

  function bindModalChoices(sectionRoot, config) {
    var modalEl = getModalEl(sectionRoot, config);
    if (!modalEl || modalEl.dataset.sscCollectionPreorderModalBound === '1') {
      return;
    }
    modalEl.dataset.sscCollectionPreorderModalBound = '1';

    bindModalCloseRestore(sectionRoot, config);

    var sectionId = String(config.sectionId);

    modalEl.addEventListener('click', function (evt) {
      var choiceBtn =
        evt.target && evt.target.closest
          ? evt.target.closest('[data-ssc-collection-preorder-choice]')
          : null;
      if (!choiceBtn || !modalEl.contains(choiceBtn)) return;

      var choice = choiceBtn.getAttribute('data-ssc-collection-preorder-choice');
      if (choice !== 'stock' && choice !== 'preorder') return;

      evt.preventDefault();
      executeChoice(sectionId, choice, sectionRoot, config);
    });
  }

  function bindAtcHandlers(sectionRoot, config) {
    if (
      !config.enablePreorderCollectionAtc ||
      sectionRoot.dataset.sscCollectionPreorderAtcBound === '1'
    ) {
      return;
    }
    sectionRoot.dataset.sscCollectionPreorderAtcBound = '1';

    bindModalChoices(sectionRoot, config);

    sectionRoot.addEventListener(
      'click',
      function (evt) {
        onQuickAddClick(evt, sectionRoot, config);
      },
      true,
    );
  }

  function initSection(sectionRoot) {
    if (!sectionRoot || sectionRoot.dataset.sscCollectionPreorderInited === '1') {
      return;
    }

    var config = getSectionConfig(sectionRoot);
    if (!sectionShouldInit(config)) return;

    sectionRoot.dataset.sscCollectionPreorderInited = '1';

    bindAtcHandlers(sectionRoot, config);
    bindLabelSublineHandlers(sectionRoot, config);
  }

  function bootstrap() {
    var sections = document.querySelectorAll('[data-ssc-collection-preorder]');
    for (var i = 0; i < sections.length; i++) initSection(sections[i]);
  }

  document.addEventListener('shopify:section:load', function (e) {
    if (!e.target || !e.target.querySelector) return;
    if (e.target.matches && e.target.matches('[data-ssc-collection-preorder]')) {
      initSection(e.target);
      return;
    }
    var nested = e.target.querySelector('[data-ssc-collection-preorder]');
    if (nested) initSection(nested);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
  } else {
    bootstrap();
  }

  window.ssc = window.ssc || {};
  window.ssc.MappedPreorderCollection = {
    initSection: initSection,
    getSectionConfig: getSectionConfig,
    getModal: getModal,
    getCardData: getCardData,
    updateLabelSublineForButton: updateLabelSublineForButton,
  };
})();
